import { UrlHelper } from 'hoc/UrlHelper';
import { isCordova } from 'utils/platform';

export type InternalNav = {
  to: string;
  replace?: boolean;
  state?: any;
};

export type ExternalNav = {
  externalUrl: string;
  target?: '_blank' | '_self' | '_system';
};

export type NavigationDescriptor = InternalNav | ExternalNav;

export function applyNavigation(
  desc: NavigationDescriptor | undefined | null,
): void {
  if (!desc) return;

  if ('externalUrl' in desc) {
    const target = desc.target || '_self';
    window.open(desc.externalUrl, target);
    return;
  }

  if ('to' in desc) {
    const to = desc.to.startsWith('/') ? desc.to : `/${desc.to}`;
    if (desc.replace) {
      window.location.replace(to);
    } else {
      window.location.assign(to);
    }
  }
}

const ensureLeadingSlash = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

const isAbsoluteHttpUrl = (url: string) => /^https?:\/\//i.test(url);

const methods = {
  goTo: function (url: string, openInNewTab = false): NavigationDescriptor {
    if (PLATFORM === 'cordova' && url.includes('/invite/')) {
      if (isAbsoluteHttpUrl(url)) {
        return { externalUrl: url, target: '_system' };
      }
      const fixedPath = ensureLeadingSlash(url);
      return { externalUrl: `${URL_HOST}${fixedPath}`, target: '_system' };
    }

    if (isAbsoluteHttpUrl(url)) {
      return { externalUrl: url, target: openInNewTab ? '_blank' : '_self' };
    }

    return { to: ensureLeadingSlash(url) };
  },
  goBack: function (): NavigationDescriptor | undefined {
    const href = window.location.href;
    if (href.indexOf('/messages/') >= 0) {
      return { to: '/messages/' };
    }
    if (href.indexOf('/action/') >= 0) {
      return { to: '/action/' };
    }
    if (href.indexOf('/schedule/') >= 0) {
      return { to: '/schedule/' };
    }
    return undefined;
  },

  howItWorks: function (): NavigationDescriptor {
    return {
      externalUrl: UrlHelper.url('/info/how-it-works/'),
      target: '_blank',
    };
  },
  myOffers: function (): NavigationDescriptor {
    return { externalUrl: UrlHelper.url('/dashboard/listings/') };
  },
  withlocals: function (): NavigationDescriptor {
    return { externalUrl: UrlHelper.getBaseUrl() };
  },
  myAssignments: function (): NavigationDescriptor {
    return { externalUrl: UrlHelper.url('/dashboard/hosts/') };
  },
  help: function (): NavigationDescriptor {
    return { externalUrl: 'https://support.withlocals.com', target: '_blank' };
  },
  voucher: function (tripId, guestId): NavigationDescriptor {
    const url = UrlHelper.url(
      '/pdfs/vouchers/' + tripId + '/' + guestId + '/',
      false,
    );
    // Caller should execute window.open with provided target
    if (isCordova()) {
      return { externalUrl: url, target: '_system' };
    }
    return { externalUrl: url, target: '_blank' };
  },
  findThingsToDoIn: function (
    country?: string,
    city?: string,
  ): NavigationDescriptor {
    let path = `/s/`;
    if (!!country) {
      path += `${country.replace(/ /g, '-').toLowerCase()}/`;
      if (!!city) {
        path += `${city.replace(/ /g, '-').toLowerCase()}/`;
      }
    }
    return { to: ensureLeadingSlash(path) };
  },
  toSelectedSchedule(
    bookingId: string | number,
    search?: string,
  ): NavigationDescriptor {
    const base = `/schedule/${bookingId}/details/`;
    const full = search
      ? `${base}${search.startsWith('?') ? '' : '?'}${search}`
      : base;
    return { to: ensureLeadingSlash(full) };
  },
  toSelectedMessage(contactId: string | number): NavigationDescriptor {
    const url = `/messages/${contactId}/chat/`;
    return { to: ensureLeadingSlash(url) };
  },
};

export default {
  menu: methods,
};
